/**
 * Llamadas HTTP propias del rediseño de Biblioteca (20.H):
 *   · búsqueda global (lexicón ∪ colección, EN+ES) — GET /lexicon/search
 *   · registro de partidas de práctica — POST /practice-runs (acredita
 *     `practiced` en user_word_usage sólo para producción oral, D4)
 *   · save enriquecido al guardar desde el catálogo (el endpoint ya acepta
 *     ipa/pos/cefr/example; mandarlos evita entradas pobres en Lo mío)
 *
 * Mismo patrón delgado que lexiconApi (cookie httpOnly same-origin).
 */
import type { LibraryGameId } from './libData.js';
import { apiFetch } from '../../lib/apiClient.js';

const BASE = '/api/modules/english';

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(`${BASE}${path}`, {
      ...(init?.body != null ? { headers: { 'content-type': 'application/json' } } : {}),
      ...init,
    });
  } catch {
    throw new Error('No pudimos conectar con el servidor.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { data?: T } & T;
  // Las rutas english devuelven envelope { success, data }; las de lexicon, plano.
  return json.data ?? json;
}

/** Guardado enriquecido desde el catálogo (Fase 0.3 — el backend ya acepta los campos). */
export function saveCatalogWord(input: {
  word: string;
  translation?: string;
  ipa?: string;
  partOfSpeech?: string;
  cefr?: string;
  example?: string;
}): Promise<{ id: string; alreadyExisted: boolean }> {
  const body: Record<string, string> = { word: input.word };
  if (input.translation) body.translation = input.translation;
  if (input.ipa) body.ipa = input.ipa;
  if (input.partOfSpeech) body.partOfSpeech = input.partOfSpeech;
  if (input.cefr) body.cefr = input.cefr;
  if (input.example) body.example = input.example;
  return jsonFetch<{ id: string; alreadyExisted: boolean }>('/vocab/save', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Registra una partida. `items` con el texto EXACTO de cada palabra jugada. */
export function recordPracticeRun(input: {
  runId: string;
  game: LibraryGameId;
  deck: string;
  items: Array<{ text: string; ok: boolean }>;
}): Promise<{ runId: string; credited: number }> {
  return jsonFetch<{ runId: string; credited: number }>('/practice-runs', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
